import { apiConfig, endpoints } from '@/config/api';
import type { AuthResponse } from '@/types';
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';

/**
 * API Client for Azure Functions Backend
 *
 * This client handles all HTTP communication with the Azure backend.
 * Features:
 * - Automatic token injection
 * - Request/response interceptors with transparent token refresh
 * - Error handling and retry logic
 * - TypeScript type safety
 */

class ApiClient {
  private client: AxiosInstance;
  private accessToken: string | null = null;
  private isRefreshing = false;
  private refreshQueue: Array<{
    resolve: (token: string) => void;
    reject: (err: unknown) => void;
  }> = [];

  constructor() {
    this.client = axios.create({
      baseURL: apiConfig.baseURL,
      timeout: apiConfig.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Seed token immediately from localStorage so any early API calls are authenticated
    const storedToken = localStorage.getItem('accessToken');
    if (storedToken) {
      this.accessToken = storedToken;
    }

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor - add auth token to all requests
    this.client.interceptors.request.use(
      (config) => {
        if (this.accessToken) {
          config.headers.Authorization = `Bearer ${this.accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - transparent token refresh on 401
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          const storedRefreshToken = localStorage.getItem('refreshToken');
          if (!storedRefreshToken) {
            // No refresh token - log out immediately
            this.clearToken();
            window.dispatchEvent(new CustomEvent('auth:logout'));
            return Promise.reject(error);
          }

          if (this.isRefreshing) {
            // Another refresh is already in flight - queue this request
            return new Promise<AxiosResponse>((resolve, reject) => {
              this.refreshQueue.push({
                resolve: (newToken) => {
                  if (originalRequest.headers) {
                    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                  } else {
                    originalRequest.headers = { Authorization: `Bearer ${newToken}` };
                  }
                  resolve(this.client(originalRequest));
                },
                reject,
              });
            });
          }

          this.isRefreshing = true;

          try {
            // Call refresh endpoint directly (avoids circular import with authService)
            const response = await axios.post<AuthResponse>(
              `${apiConfig.baseURL}${endpoints.auth.refresh}`,
              { refreshToken: storedRefreshToken },
              { headers: { 'Content-Type': 'application/json' } }
            );

            const { accessToken, refreshToken: newRefreshToken, userId } = response.data;

            // Persist new tokens
            this.accessToken = accessToken;
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', newRefreshToken);
            localStorage.setItem('userId', userId);

            // Drain the queue with the new token
            this.refreshQueue.forEach(({ resolve }) => resolve(accessToken));
            this.refreshQueue = [];

            // Retry the original request
            if (originalRequest.headers) {
              originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
            } else {
              originalRequest.headers = { Authorization: `Bearer ${accessToken}` };
            }
            return this.client(originalRequest);
          } catch (refreshError) {
            // Refresh failed - log out and reject all queued requests
            this.refreshQueue.forEach(({ reject }) => reject(refreshError));
            this.refreshQueue = [];
            this.clearToken();
            window.dispatchEvent(new CustomEvent('auth:logout'));
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Set the access token for authenticated requests
   */
  setToken(token: string) {
    this.accessToken = token;
  }

  /**
   * Clear the access token
   */
  clearToken() {
    this.accessToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
  }

  /**
   * GET request
   */
  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, config);
    return response.data;
  }

  /**
   * POST request
   */
  async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, data, config);
    return response.data;
  }

  /**
   * PUT request
   */
  async put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(url, data, config);
    return response.data;
  }

  /**
   * PATCH request
   */
  async patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(url, data, config);
    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url, config);
    return response.data;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
